import * as XLSX from "xlsx-js-style";
import { currencyName } from "./formatters";

/**
 * A column in the sheet.
 *
 * A bare string is the field name, exported raw — how every column was declared before. The
 * object form exists for the cells that must not go out raw: **money**. A currency figure without
 * its symbol is not a smaller version of the truth, it is a different number, and on PostmanAL
 * the same column can hold LEK on one row and EUR on the next.
 *
 * `value` receives the row and returns what the cell should say. It runs on whichever rows are
 * exported — the caller's prepared data, or the raw grid rows when the user has ticked a
 * selection — so it must read the row's own fields rather than anything pre-formatted.
 */
export type ExportColumn = {
	/** Field on the row, and the grid column id the header is read from. */
	colId: string;
	/** Overrides the grid's header. Only needed for a column the grid does not have. */
	headerName?: string;
	/**
	 * The cell value. Defaults to the raw field.
	 *
	 * May also return a SheetJS cell object (`{ t, v, z }`) when the cell needs a type or a number
	 * format of its own — which is how {@link moneyExportColumns} keeps an amount numeric while
	 * still printing two decimals.
	 */
	value?: (row: any) => any;
};

export type ExportColumnSpec = string | ExportColumn;

/** Excel number format for an exported amount: two decimals, thousands separated, no symbol. */
const MONEY_NUMBER_FORMAT = "#,##0.00";

/** Default header of a generated currency column. */
const CURRENCY_COLUMN_HEADER = "Valuta";

/** Fixed currency id, or one read off each row. */
export type ExportCurrency =
	| number
	| null
	| undefined
	| ((row: any) => number | null | undefined);

export type MoneyExportColumnsOptions = {
	/** Field holding the amount, and the grid column id the header is read from. */
	colId: string;
	/** Overrides the grid's header for the amount column. */
	headerName?: string;
	/** Header of the generated currency column. Defaults to `Valuta`. */
	currencyHeaderName?: string;
	/** Which currency the amount is in — a constant, or a function reading the row. */
	currency: ExportCurrency;
	/** The amount, if it is not simply `row[colId]`. */
	amount?: (row: any) => any;
	/** From `useCurrencies()`. False collapses back to the single column the sheet had before. */
	dualCurrencyEnabled: boolean;
	/** What that single column prints off PostmanAL. Defaults to the raw amount. */
	single?: (row: any) => any;
};

const toAmount = (raw: any): number | null => {
	if (raw === null || raw === undefined || raw?.toString().trim() === "") {
		return null;
	}
	const amount = Number(raw);
	return Number.isFinite(amount) ? amount : null;
};

/**
 * A money figure as **two adjacent columns**: the amount, then the currency it is in.
 *
 * ## Why
 *
 * These sheets used to carry the currency inside the amount — `6,000.00 L`, `85.00 €`. On a sheet
 * forty columns wide the symbol is a single glyph at the end of a number that staff had to hunt
 * for, and on PostmanAL the same column holds LEK on one row and EUR on the next, so two figures
 * that must never be added looked identical at a glance. Worse, an amount carrying its symbol as
 * *text* is no longer a number to Excel: it cannot be summed, sorted or filtered, which is most of
 * what a spreadsheet is for.
 *
 * Splitting it fixes both. The amount stays numeric (`t: "n"`, two decimals via a number format,
 * not via string padding) and the currency gets a column of its own that reads `EURO` or `LEK`.
 *
 * ## The gate lives in here
 *
 * Off PostmanAL this returns a **single** column formatted exactly as that sheet formatted it
 * before, so Kosovo and Macedonia — one currency each, where a column repeating it on every row
 * would be pure noise — export what they have always exported. Callers never write the feature
 * check themselves, the same reasoning that put the gate inside `dualMoneyColumns`. Spread the
 * result into the column list:
 *
 * ```ts
 * ...moneyExportColumns({
 *     colId: "orderPrice",
 *     currency: (row) => row?.orderCurrencyId ?? defaultCurrencyId,
 *     currencyHeaderName: "Valuta e vlerës",
 *     dualCurrencyEnabled,
 * }),
 * ```
 */
export const moneyExportColumns = ({
	colId,
	headerName,
	currencyHeaderName,
	currency,
	amount,
	dualCurrencyEnabled,
	single,
}: MoneyExportColumnsOptions): ExportColumnSpec[] => {
	const rawAmount = (row: any) => (amount ? amount(row) : row?.[colId]);

	if (!dualCurrencyEnabled) {
		return [{ colId, headerName, value: single ?? rawAmount }];
	}

	const readCurrency = (row: any) =>
		typeof currency === "function" ? currency(row) : currency;

	return [
		{
			colId,
			headerName,
			value: (row: any) => {
				const value = toAmount(rawAmount(row));
				// Empty rather than 0: a settlement with no side in this currency had no such
				// amount, and a zero invites someone to ask which orders made it up.
				if (value === null) return "";
				return { t: "n", v: value, z: MONEY_NUMBER_FORMAT };
			},
		},
		{
			// No grid column answers to this id — the header always comes from here.
			colId: `${colId}Currency`,
			headerName: currencyHeaderName ?? CURRENCY_COLUMN_HEADER,
			value: (row: any) =>
				toAmount(rawAmount(row)) === null
					? ""
					: currencyName(readCurrency(row)),
		},
	];
};

/**
 * AG-Grid's own export (right-click → Export to Excel / CSV), printing what the grid prints.
 *
 * v27 exports `valueService.getValue()`: the value after `valueGetter`, but **before**
 * `valueFormatter`. Every money column here adds its currency symbol in a `valueFormatter`, so
 * the context-menu export produced bare numbers — `6000` where the cell reads `6000.00 L` — and
 * a LEK order and a EUR order came out as two indistinguishable figures.
 *
 * `processCellCallback` is the hook v27 gives for this; `useValueFormatterForExport` arrived in
 * v28. Pass to **both** `defaultExcelExportParams` and `defaultCsvExportParams` — they are
 * separate menu items and each takes its own params.
 */
export const formattedExportParams = {
	processCellCallback: (params: any) => {
		const colDef = params?.column?.getColDef?.();
		const formatter = colDef?.valueFormatter;
		if (typeof formatter !== "function") {
			return params?.value ?? "";
		}
		try {
			return formatter({
				...params,
				data: params?.node?.data,
				colDef,
			});
		} catch (error) {
			// A formatter that wants more of the grid than an export can hand it should not cost
			// the user their whole spreadsheet.
			return params?.value ?? "";
		}
	},
};

export const exportDataToCsv = (
	gridRef: any,
	selectedColumnIds: ExportColumnSpec[],
	data?: any[]
) => {
	const selectedRows = gridRef.current?.api?.getSelectedRows();
	const onlySelected = selectedRows && selectedRows.length > 0;

	const columns: ExportColumn[] = selectedColumnIds.map((column) =>
		typeof column === "string" ? { colId: column } : column
	);

	const date = new Date();
	const formattedDate = `${date.getDate().toString().padStart(2, "0")}-${(
		date.getMonth() + 1
	)
		.toString()
		.padStart(2, "0")}-${date.getFullYear()}`;

	const randomNumber = Math.floor(Math.random() * 1000)
		.toString()
		.padStart(3, "0");

	const fileName = `${formattedDate}_${randomNumber}.xlsx`;

	let dataToExport: any[] = [];
	if (!data) {
		gridRef.current?.api.forEachNode((node: any) =>
			dataToExport.push(node.data)
		);
	} else {
		dataToExport = data;
	}

	// Whatever the user ticked, else everything — which is what `dataToExport` was gathered for.
	// It used to fall back to `data`, so a caller that passes no data (and there are several)
	// exported nothing at all and threw on the map below.
	const rowData = onlySelected ? selectedRows : dataToExport;

	// Create a new workbook
	const wb = XLSX.utils.book_new();

	// Extract the headers based on the requested columns
	const headers = columns.map(
		(column) =>
			column.headerName ??
			gridRef.current?.api?.getColumnDef(column.colId)?.headerName ??
			column.colId
	);

	// Create a styled header row
	const headerRow = headers.map((header) => {
		return {
			t: "s",
			v: header,
			s: {
				font: {
					name: "Calibri",
					sz: 12,
					bold: true,
					color: { rgb: "FFFFFF" },
				},
				fill: {
					fgColor: { rgb: "6d31a2" }, // Background color #4f16c2
				},
				alignment: {
					vertical: "center",
					horizontal: "start",
				},
			},
		};
	});

	// Map row data to selected column keys
	const sheetData = rowData.map((row: any) =>
		columns.map((column) => {
			const cell = column.value ? column.value(row) : row?.[column.colId];
			return cell ?? "";
		})
	);

	// Combine header and data rows
	const wsData = [headerRow, ...sheetData];

	// Create the worksheet
	const ws = XLSX.utils.aoa_to_sheet(wsData);

	// Set row height for the header
	ws["!rows"] = [{ hpx: 20 }]; // Header row height: 40 pixels

	// Optional: Set column widths
	const wscols = columns.map(() => ({ wpx: 110 })); // Column width: 150 pixels
	ws["!cols"] = wscols;

	// Append the worksheet to the workbook
	XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

	// Trigger the download
	XLSX.writeFile(wb, fileName);
};
